import React from 'react';
import type { ToastType, UserProfile } from '../types';
import { resolveRoddoDarkMode } from '../constants/theme';
import { sellerMakeMarketplaceItemAvailable, sellerMarkMarketplaceItemSold, sellerSetMarketplaceItemPending } from '../services/marketplace';
import {
  clearBazaarDMConversationForMe,
  deleteBazaarDMMessage,
  editBazaarDMMessage,
  markBazaarDMRead,
  reportBazaarDMMessage,
  sendBazaarDMMessage,
  sendBazaarDMOfferAction,
  type BazaarDMMessage,
  type BazaarDMOffer,
} from '../services/bazaarDMs';
import {
  getBazaarDMThreadSnapshot,
  startBazaarDMThreadStore,
  subscribeBazaarDMThreadStore,
} from '../services/bazaarDMThreadStore';

const clean = (value: unknown) => String(value ?? '').trim();

const fmtTime = (ms?: number) => {
  if (!ms) return '';
  try {
    return new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit' }).format(new Date(ms));
  } catch {
    return '';
  }
};

function formatMoney(cents?: number, currency?: string) {
  const value = Math.max(0, Math.round(Number(cents || 0)));
  const code = clean(currency || 'usd').toUpperCase() || 'USD';
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: code }).format(value / 100);
  } catch {
    return `${code} ${(value / 100).toFixed(2)}`;
  }
}

type Props = {
  threadId: string;
  listingId: string;
  listingTitle: string;
  listingImage?: string;
  listingPriceCents?: number;
  listingCurrency?: string;
  listingStatus?: string;
  sellerId: string;
  sellerName: string;
  sellerAvatar?: string;
  buyerId: string;
  buyerName: string;
  buyerAvatar?: string;
  startWithOffer?: boolean;
  currentUser: UserProfile;
  isDarkMode?: boolean;
  addToast: (message: string, type?: ToastType) => void;
  onListingStatusChange?: (status: 'active' | 'pending' | 'sold') => void;
  onClose: () => void;
};

export default function BazaarDMView({
  threadId,
  listingId,
  listingTitle,
  listingImage,
  listingPriceCents,
  listingCurrency,
  listingStatus = 'active',
  sellerId,
  sellerName,
  sellerAvatar,
  buyerId,
  buyerName,
  buyerAvatar,
  startWithOffer = false,
  currentUser,
  isDarkMode: explicitIsDarkMode,
  addToast,
  onListingStatusChange,
  onClose,
}: Props) {
  const uid = clean((currentUser as any)?.uid);
  const isDarkMode = resolveRoddoDarkMode(explicitIsDarkMode);
  const [threadSnapshot, setThreadSnapshot] = React.useState(() =>
    getBazaarDMThreadSnapshot(uid, threadId),
  );
  const [optimisticThreadExists, setOptimisticThreadExists] = React.useState(false);
  const rawMessages: BazaarDMMessage[] = threadSnapshot.messages;
  const messages: BazaarDMMessage[] = rawMessages.filter((message) => !(message.status === 'deleted' && message.senderId === uid));
  const offers: BazaarDMOffer[] = threadSnapshot.offers;
  const threadExists = threadSnapshot.threadExists || optimisticThreadExists;
  const threadReady = threadSnapshot.threadReady || optimisticThreadExists;
  const threadContentReady =
    threadReady &&
    (!threadExists || (threadSnapshot.messagesReady && threadSnapshot.offersReady));
  const [offerOpen, setOfferOpen] = React.useState(startWithOffer);
  const [offerAmount, setOfferAmount] = React.useState('');
  const [offerNote, setOfferNote] = React.useState('');
  const [offerTargetId, setOfferTargetId] = React.useState('');
  const [text, setText] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [messageMenuId, setMessageMenuId] = React.useState('');
  const [editingMessageId, setEditingMessageId] = React.useState('');
  const [editingText, setEditingText] = React.useState('');
  const [reportingMessage, setReportingMessage] = React.useState<BazaarDMMessage | null>(null);
  const [reportReason, setReportReason] = React.useState('harassment');
  const [reportDetails, setReportDetails] = React.useState('');
  const [pendingOfferIds, setPendingOfferIds] = React.useState<Set<string>>(() => new Set());
  const [localListingStatus, setLocalListingStatus] = React.useState(clean(listingStatus).toLowerCase() || 'active');
  const [listingLifecycleAction, setListingLifecycleAction] = React.useState<'pending' | 'sold' | 'available' | ''>('');
  const [confirmMarkSoldOpen, setConfirmMarkSoldOpen] = React.useState(false);
  const [conversationMenuOpen, setConversationMenuOpen] = React.useState(false);
  const [confirmClearHistoryOpen, setConfirmClearHistoryOpen] = React.useState(false);
  const [clearingHistory, setClearingHistory] = React.useState(false);
  const submitLockRef = React.useRef(false);
  const scrollRef = React.useRef<HTMLDivElement | null>(null);
  const textareaRef = React.useRef<HTMLTextAreaElement | null>(null);

  React.useEffect(() => {
    setLocalListingStatus(clean(listingStatus).toLowerCase() || 'active');
  }, [listingStatus, listingId]);

  const peerName = uid === sellerId ? buyerName : sellerName;
  const peerAvatar = uid === sellerId ? buyerAvatar : sellerAvatar;

  const styles = React.useMemo(() => ({
    text: isDarkMode ? '#F2F6FC' : '#30271E',
    soft: isDarkMode ? '#A8B4C7' : '#6E6255',
    faint: isDarkMode ? '#74839A' : '#8B7D6D',
    shell: isDarkMode ? '#070B14' : '#FFFDF8',
    surface: isDarkMode ? '#101827' : '#FFFFFF',
    surfaceRaised: isDarkMode ? '#0D1422' : '#F7F2E8',
    border: isDarkMode ? 'rgba(226,232,240,0.18)' : 'rgba(74,58,42,0.14)',
    ownBubble: isDarkMode ? '#233251' : '#E9F2FF',
    theirBubble: isDarkMode ? '#121C2E' : '#F2EBE1',
    accent: isDarkMode ? '#F0C86D' : '#2F80ED',
  }), [isDarkMode]);

  React.useEffect(() => {
    setOptimisticThreadExists(false);
    startBazaarDMThreadStore(uid, threadId);
    const sync = () => setThreadSnapshot(getBazaarDMThreadSnapshot(uid, threadId));
    sync();
    return subscribeBazaarDMThreadStore(uid, threadId, sync);
  }, [uid, threadId]);

  React.useEffect(() => {
    if (!uid || !threadId || !threadExists) return;
    void markBazaarDMRead(uid, threadId);
  }, [uid, threadId, threadExists, messages.length]);

  React.useEffect(() => {
    const raf = window.requestAnimationFrame(() => {
      const el = scrollRef.current;
      if (el) el.scrollTop = el.scrollHeight;
    });
    return () => window.cancelAnimationFrame(raf);
  }, [messages.length]);

  const send = async () => {
    const body = clean(text);
    if (!body || busy || submitLockRef.current) return;
    submitLockRef.current = true;
    try {
      setBusy(true);
      await sendBazaarDMMessage({
        currentUser,
        listingId,
        listingTitle,
        listingImage,
        listingPriceCents,
        listingCurrency,
        sellerId,
        sellerName,
        sellerAvatar,
        buyerId,
        buyerName,
        buyerAvatar,
        text: body,
      });
      setOptimisticThreadExists(true);
      setText('');
      window.requestAnimationFrame(() => {
        try { textareaRef.current?.focus({ preventScroll: true }); }
        catch { textareaRef.current?.focus(); }
      });
    } catch (error: any) {
      console.error('[BazaarDMView] send failed', error);
      addToast(clean(error?.message) || 'Could not send Bazaar message.', 'error');
    } finally {
      submitLockRef.current = false;
      setBusy(false);
    }
  };

  const submitOfferAction = async (action: 'offer' | 'counter' | 'accept' | 'decline', offer?: BazaarDMOffer) => {
    if (busy || submitLockRef.current) return;
    const needsAmount = action === 'offer' || action === 'counter';
    const amountCents = needsAmount ? Math.round(Number(offerAmount) * 100) : Number(offer?.amountCents || 0);
    if (needsAmount && (!Number.isFinite(amountCents) || amountCents < 50)) { addToast('Enter an offer of at least $0.50.', 'error'); return; }
    submitLockRef.current = true;
    try {
      setBusy(true);
      await sendBazaarDMOfferAction({ currentUser, listingId, listingTitle, listingImage, listingPriceCents, listingCurrency, sellerId, sellerName, sellerAvatar, buyerId, buyerName, buyerAvatar, action, offerId: offer?.id || offerTargetId || undefined, amountCents, note: offerNote });
      setOptimisticThreadExists(true); setOfferOpen(false); setOfferAmount(''); setOfferNote(''); setOfferTargetId('');
      addToast(action === 'accept' ? 'Offer accepted.' : action === 'decline' ? 'Offer declined.' : action === 'counter' ? 'Counter offer sent.' : 'Offer sent.', 'success');
    } catch (error: any) { console.error('[BazaarDMView] offer action failed', error); addToast(clean(error?.message) || 'Could not update the offer.', 'error'); }
    finally { submitLockRef.current = false; setBusy(false); }
  };

  const markAcceptedOfferPending = async (offer: BazaarDMOffer) => {
    if (uid !== sellerId || busy || listingLifecycleAction || pendingOfferIds.has(offer.id)) return;
    try {
      setListingLifecycleAction('pending');
      await sellerSetMarketplaceItemPending(listingId, { threadId, offerId: offer.id, buyerId });
      setPendingOfferIds((current) => new Set(current).add(offer.id));
      setLocalListingStatus('pending');
      onListingStatusChange?.('pending');
      addToast('Listing marked pending.', 'success');
    } catch (error: any) {
      console.error('[BazaarDMView] mark pending failed', error);
      addToast(clean(error?.message) || 'Could not mark the listing pending.', 'error');
    } finally {
      setListingLifecycleAction('');
    }
  };

  const markListingSoldFromChat = async () => {
    if (uid !== sellerId || busy || listingLifecycleAction) return;
    try {
      setListingLifecycleAction('sold');
      await sellerMarkMarketplaceItemSold(listingId);
      setLocalListingStatus('sold');
      onListingStatusChange?.('sold');
      addToast('Listing marked sold.', 'success');
      setConfirmMarkSoldOpen(false);
    } catch (error: any) {
      console.error('[BazaarDMView] mark sold failed', error);
      addToast(clean(error?.message) || 'Could not mark the listing sold.', 'error');
    } finally {
      setListingLifecycleAction('');
    }
  };

  const makeListingAvailableFromChat = async () => {
    if (uid !== sellerId || busy || listingLifecycleAction) return;
    try {
      setListingLifecycleAction('available');
      await sellerMakeMarketplaceItemAvailable(listingId);
      setLocalListingStatus('active');
      onListingStatusChange?.('active');
      setPendingOfferIds(new Set());
      addToast('Listing is available again.', 'success');
    } catch (error: any) {
      console.error('[BazaarDMView] make available failed', error);
      addToast(clean(error?.message) || 'Could not make the listing available.', 'error');
    } finally {
      setListingLifecycleAction('');
    }
  };

  const LoadingDots = () => (
    <span className="ml-1 inline-flex items-end gap-0.5" aria-label="Loading">
      {[0, 1, 2].map((dot) => (
        <span key={dot} className="inline-block h-1 w-1 rounded-full bg-current animate-bounce" style={{ animationDelay: `${dot * 120}ms`, animationDuration: '720ms' }} />
      ))}
    </span>
  );

  const beginEditMessage = (message: BazaarDMMessage) => {
    if (message.messageType || message.status === 'deleted' || message.senderId !== uid) return;
    setMessageMenuId('');
    setEditingMessageId(message.id);
    setEditingText(message.text);
  };

  const saveMessageEdit = async () => {
    const next = clean(editingText);
    if (!editingMessageId || !next || busy) return;
    try {
      setBusy(true);
      await editBazaarDMMessage(threadId, editingMessageId, next);
      setEditingMessageId('');
      setEditingText('');
      addToast('Message updated.', 'success');
    } catch (error: any) {
      console.error('[BazaarDMView] edit failed', error);
      addToast(clean(error?.message) || 'Could not edit the message.', 'error');
    } finally {
      setBusy(false);
    }
  };

  const removeMessage = async (message: BazaarDMMessage) => {
    if (busy || message.messageType || message.senderId !== uid) return;
    if (!window.confirm('Delete this message?')) return;
    setMessageMenuId('');
    try {
      setBusy(true);
      await deleteBazaarDMMessage(threadId, message.id);
      addToast('Message deleted.', 'success');
    } catch (error: any) {
      console.error('[BazaarDMView] delete failed', error);
      addToast(clean(error?.message) || 'Could not delete the message.', 'error');
    } finally {
      setBusy(false);
    }
  };

  const clearHistoryForMe = async () => {
    if (!threadExists || clearingHistory) return;
    setConversationMenuOpen(false);
    try {
      setClearingHistory(true);
      await clearBazaarDMConversationForMe(threadId);
      addToast('Conversation history cleared for you. Offer history stays available.', 'success');
    } catch (error: any) {
      console.error('[BazaarDMView] clear history failed', error);
      addToast(clean(error?.message) || 'Could not clear this conversation.', 'error');
    } finally {
      setClearingHistory(false);
      setConfirmClearHistoryOpen(false);
    }
  };

  const submitMessageReport = async () => {
    if (!reportingMessage || busy) return;
    try {
      setBusy(true);
      await reportBazaarDMMessage({
        threadId,
        message: reportingMessage,
        listingId,
        targetUserId: reportingMessage.senderId,
        reason: reportReason,
        details: reportDetails,
      });
      setReportingMessage(null);
      setReportDetails('');
      setReportReason('harassment');
      addToast('Message reported for review.', 'success');
    } catch (error: any) {
      console.error('[BazaarDMView] report failed', error);
      addToast(clean(error?.message) || 'Could not report the message.', 'error');
    } finally {
      setBusy(false);
    }
  };

  const beginCounter = (offer: BazaarDMOffer) => { setOfferTargetId(offer.id); setOfferAmount((offer.amountCents / 100).toFixed(2)); setOfferNote(''); setOfferOpen(true); };
  const offerById = new Map(offers.map((offer) => [offer.id, offer]));
  const latestAcceptedLifecycleMessageId = [...messages].reverse().find((message) => {
    if (message.messageType !== 'offer' || message.offerAction !== 'accept' || !message.offerId) return false;
    return offerById.get(message.offerId)?.status === 'agreed';
  })?.id || '';

  return (
    <div className="relative flex h-[min(72dvh,680px)] min-h-[430px] flex-col" style={{ color: styles.text }}>
      <div className="flex items-center gap-3 border-b pb-3" style={{ borderColor: styles.border }}>
        <div className="h-12 w-12 shrink-0 overflow-hidden rounded-[12px] border" style={{ borderColor: styles.border, background: styles.surfaceRaised }}>
          {listingImage ? <img src={listingImage} alt="" className="h-full w-full object-cover" /> : <div className="grid h-full place-items-center text-[9px] font-black" style={{ color: styles.faint }}>BAZAAR</div>}
        </div>
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-black">{listingTitle || 'Bazaar listing'}</div>
          <div className="mt-0.5 text-[11px] font-bold" style={{ color: styles.soft }}>
            {listingPriceCents ? `${formatMoney(listingPriceCents, listingCurrency)} · ` : ''}{peerName || 'Angler'}
          </div>
          <div className="mt-0.5 text-[9px] font-black uppercase tracking-[0.12em]" style={{ color: styles.faint }}>Private Bazaar message</div>
        </div>
        <div className="relative flex shrink-0 items-center gap-1.5">
          <button type="button" onClick={() => setConversationMenuOpen((value) => !value)} className="grid h-10 w-10 place-items-center rounded-full border text-lg font-black" style={{ borderColor: styles.border, background: styles.surfaceRaised, color: styles.text }} aria-label="Bazaar conversation options">⋯</button>
          {conversationMenuOpen ? (
            <div className="absolute right-11 top-11 z-[70] min-w-[190px] overflow-hidden rounded-[12px] border p-1 shadow-xl" style={{ background: styles.surface, borderColor: styles.border }}>
              <button type="button" disabled={!threadExists || clearingHistory} onClick={() => { setConversationMenuOpen(false); setConfirmClearHistoryOpen(true); }} className="block w-full rounded-[9px] px-3 py-2.5 text-left text-[11px] font-black hover:bg-black/5 disabled:opacity-40 dark:hover:bg-white/5">{clearingHistory ? 'Clearing…' : 'Clear history for me'}</button>
              <div className="px-3 pb-2 text-[9px] font-semibold leading-4" style={{ color: styles.faint }}>Removes normal chat history from your view. Offer and transaction history stays.</div>
            </div>
          ) : null}
          <button type="button" onClick={onClose} className="grid h-10 w-10 place-items-center rounded-full border text-lg font-black" style={{ borderColor: styles.border, background: styles.surfaceRaised, color: styles.text }} aria-label="Back from Bazaar conversation">‹</button>
        </div>
      </div>

      <div ref={scrollRef} className="min-h-0 flex-1 space-y-2 overflow-y-auto py-4 roddo-content-scroll">
        {!threadContentReady ? (
          <div
            className="mx-auto mt-8 w-full max-w-sm rounded-[16px] border p-5"
            style={{ background: styles.surface, borderColor: styles.border }}
            aria-label="Loading Bazaar conversation"
          >
            <div className="h-3 w-28 rounded-full opacity-20" style={{ background: styles.soft }} />
            <div className="mt-3 h-9 w-[72%] rounded-[14px] opacity-15" style={{ background: styles.soft }} />
            <div className="ml-auto mt-2 h-9 w-[58%] rounded-[14px] opacity-15" style={{ background: styles.soft }} />
          </div>
        ) : !threadExists ? (
          <div className="mx-auto mt-8 max-w-sm rounded-[16px] border p-5 text-center" style={{ background: styles.surface, borderColor: styles.border }}>
            <div className="text-2xl">💬</div>
            <div className="mt-2 text-sm font-black">Start a Bazaar conversation</div>
            <div className="mt-1 text-xs font-semibold leading-5" style={{ color: styles.soft }}>
              Keep item questions, pickup details, and shipping discussion here. Do not share private fishing spots or unrelated live location.
            </div>
          </div>
        ) : null}

        {threadContentReady ? messages.map((message) => {
          const mine = message.senderId === uid;
          const deleted = message.status === 'deleted';
          const showMenu = !deleted && ((mine && !message.messageType) || !mine);
          return (
            <div key={message.id} className={`flex items-start gap-1.5 ${mine ? 'justify-end' : 'justify-start'}`}>
              {!mine && showMenu ? (
                <div className="relative mt-1 shrink-0">
                  <button type="button" onClick={() => setMessageMenuId((current) => current === message.id ? '' : message.id)} className="grid h-7 w-7 place-items-center rounded-full border text-[14px] font-black opacity-75 hover:opacity-100" style={{ color: styles.faint, borderColor: styles.border, background: styles.surfaceRaised }} aria-label="Message actions">⋯</button>
                  {messageMenuId === message.id ? (
                    <div className="absolute left-0 top-8 z-50 min-w-[132px] overflow-hidden rounded-[11px] border p-1 shadow-xl" style={{ background: styles.surface, borderColor: styles.border }}>
                      <button type="button" onClick={() => { setMessageMenuId(''); setReportingMessage(message); setReportReason('harassment'); setReportDetails(''); }} className="block w-full rounded-[8px] px-3 py-2 text-left text-[11px] font-black text-rose-600 hover:bg-rose-500/10 dark:text-rose-300">Report</button>
                    </div>
                  ) : null}
                </div>
              ) : null}
              <div className="max-w-[82%] rounded-[16px] px-3.5 py-2.5" style={{ background: deleted ? 'transparent' : mine ? styles.ownBubble : styles.theirBubble, color: deleted ? styles.faint : styles.text }}>
                {message.messageType === 'offer' && !deleted ? (() => {
                  const offer = message.offerId ? offerById.get(message.offerId) : undefined;
                  const live = offer && ['pending', 'countered'].includes(offer.status);
                  // One offer document records the current negotiation state while
                  // several historical message cards can share its offerId. Only
                  // the message representing the offer's CURRENT action is actionable.
                  // This prevents old offer/counter cards from regaining controls
                  // after the other participant responds.
                  const isCurrentAction = Boolean(
                    live
                    && message.offerAction === offer.lastAction
                    && message.senderId === offer.lastActionBy
                    && Number(message.amountCents || 0) === Number(offer.amountCents || 0)
                  );
                  const canRespond = Boolean(isCurrentAction && offer.lastActionBy !== uid);
                  const isAcceptedMessage = Boolean(
                    offer
                    && message.offerAction === 'accept'
                    && offer.status === 'agreed'
                  );
                  const isDeclinedMessage = Boolean(
                    offer
                    && message.offerAction === 'decline'
                    && offer.status === 'declined'
                  );
                  const isSupersededMessage = Boolean(
                    offer
                    && !isCurrentAction
                    && !isAcceptedMessage
                    && !isDeclinedMessage
                    && (message.offerAction === 'offer' || message.offerAction === 'counter')
                  );
                  return (
                    <div className="min-w-[210px]">
                      <div className="text-[10px] font-black uppercase tracking-[.12em]" style={{ color: styles.faint }}>{message.offerAction === 'counter' ? 'Counter offer' : message.offerAction === 'accept' ? 'Offer accepted' : message.offerAction === 'decline' ? 'Offer declined' : 'Offer'}</div>
                      <div className="mt-1 text-xl font-black">{formatMoney(message.amountCents, message.currency || listingCurrency)}</div>
                      {message.note ? <div className="mt-1 whitespace-pre-wrap break-words text-xs font-semibold">{message.note}</div> : null}
                      {canRespond ? <div className="mt-2 grid grid-cols-3 gap-1.5">
                        <button type="button" disabled={busy} onClick={() => void submitOfferAction('accept', offer)} className="rounded-[9px] bg-emerald-500/20 px-2 py-2 text-[10px] font-black text-emerald-700 dark:text-emerald-300">Accept</button>
                        <button type="button" disabled={busy} onClick={() => beginCounter(offer)} className="rounded-[9px] bg-amber-500/20 px-2 py-2 text-[10px] font-black text-amber-700 dark:text-amber-200">Counter</button>
                        <button type="button" disabled={busy} onClick={() => void submitOfferAction('decline', offer)} className="rounded-[9px] bg-rose-500/15 px-2 py-2 text-[10px] font-black text-rose-700 dark:text-rose-300">Decline</button>
                      </div> : null}
                      {isAcceptedMessage ? <div className="mt-2">
                        <div className="text-[10px] font-black text-emerald-700 dark:text-emerald-300">AGREED · Arrange payment directly with the seller.</div>
                        {uid === sellerId && message.id === latestAcceptedLifecycleMessageId ? (
                          <div className="mt-2">
                            {localListingStatus === 'pending' ? (
                              <>
                                <div className="mb-2 rounded-[9px] border border-amber-500/35 bg-amber-400/15 px-2 py-2 text-center text-[10px] font-black uppercase tracking-[.08em] text-amber-800 dark:text-amber-200">Pending</div>
                                <div className="grid grid-cols-2 gap-1.5">
                                  <button type="button" disabled={Boolean(listingLifecycleAction)} onClick={() => setConfirmMarkSoldOpen(true)} className="rounded-[9px] bg-emerald-600 px-2 py-2 text-[10px] font-black text-white disabled:opacity-50">{listingLifecycleAction === 'sold' ? <>Marking sold<LoadingDots /></> : 'Mark as sold'}</button>
                                  <button type="button" disabled={Boolean(listingLifecycleAction)} onClick={() => void makeListingAvailableFromChat()} className="rounded-[9px] border border-[#4EA5E9]/40 bg-[#4EA5E9]/15 px-2 py-2 text-[10px] font-black text-[#246FAD] disabled:opacity-50 dark:text-sky-200">{listingLifecycleAction === 'available' ? <>Updating<LoadingDots /></> : 'Available'}</button>
                                </div>
                              </>
                            ) : localListingStatus === 'sold' ? (
                              <div className="rounded-[9px] bg-emerald-600/15 px-2 py-2 text-center text-[10px] font-black uppercase tracking-[.08em] text-emerald-700 dark:text-emerald-300">Sold</div>
                            ) : (
                              <button type="button" disabled={Boolean(listingLifecycleAction) || pendingOfferIds.has(offer.id)} onClick={() => void markAcceptedOfferPending(offer)} className="w-full rounded-[9px] border border-amber-500/30 bg-amber-400/15 px-2 py-2 text-[10px] font-black text-amber-800 disabled:opacity-50 dark:text-amber-200">{listingLifecycleAction === 'pending' ? <>Marking pending<LoadingDots /></> : 'Mark listing as pending'}</button>
                            )}
                          </div>
                        ) : null}
                      </div> : null}
                      {isDeclinedMessage ? <div className="mt-2 text-[10px] font-black" style={{ color: styles.faint }}>DECLINED</div> : null}
                      {isSupersededMessage ? <div className="mt-2 text-[10px] font-black uppercase tracking-[.08em]" style={{ color: styles.faint }}>Superseded</div> : null}
                    </div>
                  );
                })() : <div className={`whitespace-pre-wrap break-words text-[13px] font-semibold leading-5 ${deleted ? 'italic' : ''}`}>{deleted ? 'Message removed' : message.text}</div>}
                <div className={`mt-1 text-[9px] font-bold ${mine ? 'text-right' : 'text-left'}`} style={{ color: styles.faint }}>{fmtTime(message.createdAt)}{message.editedAt && !deleted ? ' · Edited' : ''}</div>
              </div>
              {mine && showMenu ? (
                <div className="relative mt-1 shrink-0">
                  <button type="button" onClick={() => setMessageMenuId((current) => current === message.id ? '' : message.id)} className="grid h-7 w-7 place-items-center rounded-full border text-[14px] font-black opacity-75 hover:opacity-100" style={{ color: styles.faint, borderColor: styles.border, background: styles.surfaceRaised }} aria-label="Message actions">⋯</button>
                  {messageMenuId === message.id ? (
                    <div className="absolute right-0 top-8 z-50 min-w-[132px] overflow-hidden rounded-[11px] border p-1 shadow-xl" style={{ background: styles.surface, borderColor: styles.border }}>
                      <button type="button" onClick={() => beginEditMessage(message)} className="block w-full rounded-[8px] px-3 py-2 text-left text-[11px] font-black hover:bg-black/5 dark:hover:bg-white/5">Edit</button>
                      <button type="button" onClick={() => void removeMessage(message)} className="block w-full rounded-[8px] px-3 py-2 text-left text-[11px] font-black text-rose-600 hover:bg-rose-500/10 dark:text-rose-300">Delete</button>
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
          );
        }) : null}
      </div>

      {editingMessageId ? <div className="mb-3 rounded-[16px] border p-3" style={{ borderColor: styles.border, background: styles.surfaceRaised }}>
        <div className="flex items-center justify-between gap-3"><div className="text-xs font-black">Edit message</div><button type="button" onClick={() => { setEditingMessageId(''); setEditingText(''); }} className="text-xs font-black" style={{ color: styles.faint }}>Cancel</button></div>
        <textarea value={editingText} onChange={(e) => setEditingText(e.target.value)} rows={2} maxLength={2000} className="mt-2 w-full resize-none rounded-[11px] border bg-transparent px-3 py-2.5 text-sm font-semibold outline-none" style={{ borderColor: styles.border, color: styles.text }} />
        <button type="button" disabled={busy || !clean(editingText)} onClick={() => void saveMessageEdit()} className="mt-2 w-full rounded-[11px] bg-[#4EA5E9] px-3 py-2.5 text-xs font-black text-white disabled:opacity-40">Save edit</button>
      </div> : null}

      {reportingMessage ? <div className="mb-3 rounded-[16px] border p-3" style={{ borderColor: styles.border, background: styles.surfaceRaised }}>
        <div className="flex items-center justify-between gap-3"><div className="text-xs font-black">Report message</div><button type="button" onClick={() => setReportingMessage(null)} className="text-xs font-black" style={{ color: styles.faint }}>Cancel</button></div>
        <select value={reportReason} onChange={(e) => setReportReason(e.target.value)} className="mt-2 w-full rounded-[11px] border bg-transparent px-3 py-2.5 text-sm font-semibold outline-none" style={{ borderColor: styles.border, color: styles.text }}>
          <option value="harassment">Harassment</option><option value="scam">Scam / fraud</option><option value="spam">Spam</option><option value="unsafe">Unsafe / threatening</option><option value="other">Other</option>
        </select>
        <textarea value={reportDetails} onChange={(e) => setReportDetails(e.target.value)} rows={2} maxLength={1200} placeholder="Optional details" className="mt-2 w-full resize-none rounded-[11px] border bg-transparent px-3 py-2.5 text-sm font-semibold outline-none" style={{ borderColor: styles.border, color: styles.text }} />
        <button type="button" disabled={busy} onClick={() => void submitMessageReport()} className="mt-2 w-full rounded-[11px] bg-rose-600 px-3 py-2.5 text-xs font-black text-white disabled:opacity-40">Send report</button>
      </div> : null}

      {uid === buyerId && !offerOpen ? <button type="button" onClick={() => { setOfferTargetId(''); setOfferAmount(''); setOfferNote(''); setOfferOpen(true); }} className="mb-2 w-full rounded-[12px] border px-3 py-2.5 text-xs font-black" style={{ borderColor: styles.border, background: styles.surfaceRaised, color: styles.text }}>Make offer</button> : null}
      {offerOpen ? <div className="mb-3 rounded-[16px] border p-3" style={{ borderColor: styles.border, background: styles.surfaceRaised }}>
        <div className="flex items-center justify-between gap-3"><div><div className="text-xs font-black">{offerTargetId ? 'Counter offer' : 'Make an offer'}</div><div className="mt-0.5 text-[10px] font-bold" style={{ color: styles.soft }}>Listed at {formatMoney(listingPriceCents, listingCurrency)}</div></div><button type="button" onClick={() => { setOfferOpen(false); setOfferTargetId(''); }} className="text-xs font-black" style={{ color: styles.faint }}>Cancel</button></div>
        <div className="mt-3 grid grid-cols-[120px_1fr] gap-2"><input inputMode="decimal" value={offerAmount} onChange={(e) => setOfferAmount(e.target.value.replace(/[^0-9.]/g, ''))} placeholder="0.00" className="rounded-[11px] border bg-transparent px-3 py-2.5 text-sm font-black outline-none" style={{ borderColor: styles.border, color: styles.text }} /><input value={offerNote} onChange={(e) => setOfferNote(e.target.value)} maxLength={1000} placeholder="Optional note" className="rounded-[11px] border bg-transparent px-3 py-2.5 text-sm font-semibold outline-none" style={{ borderColor: styles.border, color: styles.text }} /></div>
        <button type="button" disabled={busy} onClick={() => void submitOfferAction(offerTargetId ? 'counter' : 'offer')} className="mt-2 w-full rounded-[11px] bg-[#F2B84B] px-3 py-2.5 text-xs font-black text-[#2D2615] disabled:opacity-40">{busy ? 'Sending…' : offerTargetId ? 'Send counter' : 'Send offer'}</button>
      </div> : null}

      {confirmClearHistoryOpen ? (
        <div
          className="absolute inset-0 z-[120] flex items-center justify-center rounded-[18px] p-4"
          style={{ background: isDarkMode ? 'rgba(4,8,16,0.78)' : 'rgba(38,28,18,0.28)', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)' }}
          onClick={() => { if (!clearingHistory) setConfirmClearHistoryOpen(false); }}
        >
          <div
            className="w-full max-w-sm rounded-[18px] border p-5 shadow-2xl"
            style={{ background: styles.surface, borderColor: styles.border, color: styles.text }}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-base font-black">Clear conversation history?</div>
                <div className="mt-2 text-xs font-semibold leading-5" style={{ color: styles.soft }}>
                  Normal messages already in this conversation will be hidden from your account. The other person keeps their copy, and Roddo keeps protected offer/moderation references. New messages will make the conversation visible again.
                </div>
              </div>
              <button type="button" disabled={clearingHistory} onClick={() => setConfirmClearHistoryOpen(false)} className="grid h-9 w-9 shrink-0 place-items-center rounded-full border text-sm font-black disabled:opacity-40" style={{ borderColor: styles.border, background: styles.surfaceRaised }}>×</button>
            </div>
            <div className="mt-5 grid grid-cols-2 gap-2">
              <button type="button" disabled={clearingHistory} onClick={() => setConfirmClearHistoryOpen(false)} className="rounded-[11px] border px-3 py-2.5 text-xs font-black transition active:scale-[0.98] disabled:opacity-40" style={{ borderColor: styles.border, background: styles.surfaceRaised }}>Keep history</button>
              <button type="button" disabled={clearingHistory} onClick={() => void clearHistoryForMe()} className="rounded-[11px] bg-rose-600 px-3 py-2.5 text-xs font-black text-white transition active:scale-[0.98] disabled:opacity-50">{clearingHistory ? <>Clearing<LoadingDots /></> : 'Clear history'}</button>
            </div>
          </div>
        </div>
      ) : null}

      {confirmMarkSoldOpen ? (
        <div
          className="absolute inset-0 z-[120] flex items-center justify-center rounded-[18px] p-4"
          style={{ background: isDarkMode ? 'rgba(4,8,16,0.78)' : 'rgba(38,28,18,0.28)', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)' }}
          onClick={() => { if (listingLifecycleAction !== 'sold') setConfirmMarkSoldOpen(false); }}
        >
          <div
            className="w-full max-w-sm rounded-[18px] border p-5 shadow-2xl"
            style={{ background: styles.surface, borderColor: styles.border, color: styles.text }}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-base font-black">Mark listing sold?</div>
                <div className="mt-2 text-xs font-semibold leading-5" style={{ color: styles.soft }}>
                  Mark “{listingTitle || 'this listing'}” as sold? It will be removed from active Bazaar listings and retained in seller history.
                </div>
              </div>
              <button type="button" disabled={listingLifecycleAction === 'sold'} onClick={() => setConfirmMarkSoldOpen(false)} className="grid h-9 w-9 shrink-0 place-items-center rounded-full border text-sm font-black disabled:opacity-40" style={{ borderColor: styles.border, background: styles.surfaceRaised }}>×</button>
            </div>
            <div className="mt-5 grid grid-cols-2 gap-2">
              <button type="button" disabled={listingLifecycleAction === 'sold'} onClick={() => setConfirmMarkSoldOpen(false)} className="rounded-[11px] border px-3 py-2.5 text-xs font-black transition active:scale-[0.98] disabled:opacity-40" style={{ borderColor: styles.border, background: styles.surfaceRaised }}>Keep pending</button>
              <button type="button" disabled={listingLifecycleAction === 'sold'} onClick={() => void markListingSoldFromChat()} className="rounded-[11px] bg-emerald-600 px-3 py-2.5 text-xs font-black text-white transition active:scale-[0.98] disabled:opacity-50">{listingLifecycleAction === 'sold' ? <>Marking sold<LoadingDots /></> : 'Mark sold'}</button>
            </div>
          </div>
        </div>
      ) : null}

      <div data-roddo-keyboard-anchor="true" className="border-t pt-3" style={{ borderColor: styles.border }}>
        <div className="flex items-end gap-2 rounded-[16px] border p-2" style={{ background: styles.surface, borderColor: styles.border }}>
          <textarea
            ref={textareaRef}
            value={text}
            onChange={(event) => setText(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                void send();
              }
            }}
            rows={1}
            maxLength={2000}
            placeholder={`Message ${peerName || 'seller'}...`}
            className="max-h-28 min-h-[42px] flex-1 resize-none bg-transparent px-2 py-2.5 text-sm font-semibold outline-none"
            style={{ color: styles.text }}
          />
          <button type="button" onClick={() => void send()} disabled={!clean(text) || busy} className="grid h-11 w-11 shrink-0 place-items-center rounded-[12px] text-[19px] font-black transition active:scale-95 disabled:opacity-35" style={{ background: styles.accent, color: isDarkMode ? '#17202E' : '#FFFFFF' }} aria-label="Send Bazaar message">➤</button>
        </div>
        <div className="mt-1.5 px-2 text-[9px] font-semibold" style={{ color: styles.faint }}>Enter to send · Shift+Enter for a new line</div>
      </div>
    </div>
  );
}
